import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  writeBatch,
  setDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
  signInWithEmailAndPassword,
  signOut,
  getAuth
} from 'firebase/auth';
import { db, auth } from '../config/firebase';

const TENANTS_COLLECTION = 'tenants';

export const tenantService = {
  // Create a new tenant
  async createTenant(tenantData) {
    try {
      console.log('=== TENANT CREATION START ===');
      console.log('Creating tenant with data:', tenantData);
      console.log('Company ID being used:', tenantData.companyId);
      
      // First, create Firebase Auth user
      console.log('Creating Firebase Auth user for:', tenantData.email);
      const userCredential = await createUserWithEmailAndPassword(auth, tenantData.email, tenantData.email);
      const firebaseUser = userCredential.user;
      console.log('Firebase Auth user created with UID:', firebaseUser.uid);
      
      // Create user document in users collection
      console.log('Creating user document for tenant');
      const userDocData = {
        name: tenantData.name,
        email: tenantData.email,
        role: 'tenant',
        companyId: tenantData.companyId,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        isTemporaryPassword: true,
        uid: firebaseUser.uid
      };
      
      await setDoc(doc(db, 'users', firebaseUser.uid), userDocData);
      console.log('User document created successfully');
      
      // Create tenant document with authUid
      const tenantDocData = {
        ...tenantData,
        authUid: firebaseUser.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active'
      };
      console.log('Creating tenant document with data:', tenantDocData);
      
      const docRef = await addDoc(collection(db, TENANTS_COLLECTION), tenantDocData);
      console.log('Tenant document created with ID:', docRef.id);

      console.log('=== TENANT CREATION COMPLETE ===');
      console.log('Tenant email:', tenantData.email);
      console.log('Tenant password (same as email):', tenantData.email);
      console.log('Firebase Auth UID:', firebaseUser.uid);
      console.log('User document ID:', firebaseUser.uid);
      console.log('Tenant document ID:', docRef.id);

      return { 
        id: docRef.id, 
        authUid: firebaseUser.uid,
        ...tenantData 
      };
    } catch (error) {
      console.error('Error in createTenant:', error);
      
      // If Firebase Auth user was created but tenant document failed, clean up
      if (error.code === 'auth/email-already-in-use') {
        throw new Error(`A user with email ${tenantData.email} already exists. Please use a different email.`);
      }
      
      throw error;
    }
  },

  // Get all tenants for a company
  async getTenantsByCompany(companyId) {
    try {
      // Validate companyId
      if (!companyId) {
        return [];
      }

      const q = query(
        collection(db, TENANTS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const tenants = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return tenants.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      throw error;
    }
  },

  // Get tenant by ID
  async getTenantById(tenantId) {
    try {
      const docRef = doc(db, TENANTS_COLLECTION, tenantId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Tenant not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Update tenant
  async updateTenant(tenantId, updateData) {
    try {
      const docRef = doc(db, TENANTS_COLLECTION, tenantId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return { id: tenantId, ...updateData };
    } catch (error) {
      throw error;
    }
  },

  // Delete tenant
  async deleteTenant(tenantId) {
    try {
      // Get tenant data first
      const tenantData = await this.getTenantById(tenantId);
      
      // Delete user document
      if (tenantData.authUid) {
        const userDocRef = doc(db, 'users', tenantData.authUid);
        await deleteDoc(userDocRef);
      }
      
      // Delete tenant document
      const tenantDocRef = doc(db, TENANTS_COLLECTION, tenantId);
      await deleteDoc(tenantDocRef);
      
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Search tenants by name or email
  async searchTenants(companyId, searchTerm) {
    try {
      // Validate companyId
      if (!companyId) {
        return [];
      }

      const q = query(
        collection(db, TENANTS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const tenants = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return tenants.filter(tenant => 
        tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.phone?.includes(searchTerm)
      );
    } catch (error) {
      throw error;
    }
  },

  // Check if email is available for tenant creation
  async checkEmailAvailability(email, companyId) {
    try {
      // Check if tenant already exists in our system
      const existingTenants = await this.getTenantsByCompany(companyId);
      const existingTenant = existingTenants.find(t => t.email === email);
      
      if (existingTenant) {
        return {
          available: false,
          message: `A tenant with email ${email} already exists in your system.`
        };
      }
      
      return {
        available: true,
        message: 'Email is available for tenant creation.'
      };
    } catch (error) {
      throw error;
    }
  },

  // Fix existing tenants by creating proper user documents
  async fixExistingTenants(companyId) {
    try {
      const tenants = await this.getTenantsByCompany(companyId);
      let fixedCount = 0;

      for (const tenant of tenants) {
        if (tenant.authUid) {
          try {
            // Check if user document already exists
            const userDocRef = doc(db, 'users', tenant.authUid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (!userDocSnap.exists()) {
              // Create user document
              await setDoc(userDocRef, {
                name: tenant.name,
                email: tenant.email,
                role: 'tenant',
                companyId: tenant.companyId,
                status: 'active',
                createdAt: new Date(),
                updatedAt: new Date(),
                isTemporaryPassword: true
              });
              fixedCount++;
            }
          } catch (error) {
            // Continue with next tenant
          }
        }
      }

      return fixedCount;
    } catch (error) {
      throw error;
    }
  },

  // Fix tenants who were incorrectly created as admin users
  async fixTenantsWithWrongRole(companyId) {
    try {
      console.log('Checking for tenants with wrong roles...');
      
      // Get all tenants for the company
      const tenants = await this.getTenantsByCompany(companyId);
      let fixedCount = 0;

      // Check each tenant's user document
      for (const tenant of tenants) {
        if (tenant.authUid) {
          try {
            const userDocRef = doc(db, 'users', tenant.authUid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              
              // If user document exists but has wrong role, fix it
              if (userData.role !== 'tenant') {
                console.log(`Fixing tenant ${tenant.email} with wrong role: ${userData.role}`);
                
                await updateDoc(userDocRef, {
                  role: 'tenant',
                  updatedAt: new Date()
                });
                fixedCount++;
              }
            }
          } catch (error) {
            console.error(`Error fixing tenant ${tenant.email}:`, error);
          }
        }
      }

      // Also check for users with tenant emails but wrong roles
      const usersQuery = query(collection(db, 'users'), where('companyId', '==', companyId));
      const usersSnapshot = await getDocs(usersQuery);
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        
        // Check if this user's email matches a tenant but has wrong role
        const matchingTenant = tenants.find(t => t.email === userData.email);
        if (matchingTenant && userData.role !== 'tenant') {
          console.log(`Fixing user ${userData.email} with wrong role: ${userData.role}`);
          
          await updateDoc(doc(db, 'users', userDoc.id), {
            role: 'tenant',
            updatedAt: new Date()
          });
          fixedCount++;
        }
      }

      console.log(`Fixed ${fixedCount} tenants with wrong roles`);
      return fixedCount;
    } catch (error) {
      console.error('Error fixing tenants with wrong roles:', error);
      throw error;
    }
  }
}; 